package com.essntl.features.itinerary.ui.edit_itinerary

import com.essntl.core.axus.domain.AxusClient
import com.essntl.core.axus.domain.AxusItinerary
import core.dateformat.utils.fromDDTOMM
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.utils.update
import com.essntl.core.utils.viewmodel.*
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.client.domain.repository.ClientRepository
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.itinerary.domain.use_case.UpdateItineraryServiceUseCase
import com.essntl.features.message.domain.repository.MessageRepository
import com.essntl.features.message.domain.useCase.SendItineraryMessageUseCase
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.domain.repository.TaskRepository
import com.essntl.features.task.domain.use_case.AddTaskUseCase
import com.essntl.features.task.domain.use_case.DeleteTaskUseCase
import com.essntl.features.task.domain.use_case.UpdateTaskUseCase
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class EditItineraryViewModel(
    private val itineraryRepository: ItineraryRepository,
    private val proposalRepository: ProposalRepository,
    private val fileRepository: FileRepository,
    private val clientRepository: ClientRepository,
    private val categoryRepository: CategoryRepository,
    private val areaRepository: AreaRepository,
    private val taskRepository: TaskRepository,
    private val messageRepository: MessageRepository,
    private val updateItineraryServiceUseCase: UpdateItineraryServiceUseCase,
    private val addTaskUseCase: AddTaskUseCase,
    private val updateTaskUseCase: UpdateTaskUseCase,
    private val deleteTaskUseCase: DeleteTaskUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val axusClient: AxusClient,
    private val toastManager: ToastManager,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<EditItineraryState>,
    sendItineraryMessageUseCase: SendItineraryMessageUseCase,
) : ViewModel {

    init {
        getAllClient()
        getAllCategory()
        getAllArea()
        getCurrentProfile()

        // Update Proposal Service Use Case
        collectLoading(
            flow = updateItineraryServiceUseCase.state.map { it.loadingSet },
            setState = { ids -> state.update { s -> s.copy(updateItineraryServiceLoading = ids) } },
        )
        collectSuccessEvent(
            flow = updateItineraryServiceUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            update = { old, new ->
                new.copy(supplier = new.supplier ?: old.supplier)
            },
            getState = { state.value.getAllItineraryService },
            setState = { s -> state.update { it.copy(getAllItineraryService = s) } },
        )


        // Add task Use Case
        collectSuccessEvent(
            flow = addTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            stateListGetSet = arrayOf(
                StateListGetSet(
                    getState = { state.value.getAllItineraryTask },
                    setState = { s -> state.update { it.copy(getAllItineraryTask = s) } },
                    // Add only itinerary tasks
                    predicate = { !it.itinerary?.id.isNullOrEmpty() },
                ),
                StateListGetSet(
                    getState = { state.value.getAllItineraryServiceTask },
                    setState = { s -> state.update { it.copy(getAllItineraryServiceTask = s) } },
                    // Add only service tasks
                    predicate = { it.itinerary?.id.isNullOrEmpty() },
                ),
            ),
        )

        // Update task Use Case
        collectLoading(
            flow = updateTaskUseCase.state.map { it.loadingSet },
            setState = { state.update { s -> s.copy(updateTaskLoading = it) } },
        )
        collectSuccessEvent(
            flow = updateTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            update = { old, new -> new.copy(itineraryService = old.itineraryService) },
            stateListGetSet = arrayOf(
                StateListGetSet(
                    getState = { state.value.getAllItineraryTask },
                    setState = { s -> state.update { it.copy(getAllItineraryTask = s) } },
                ),
                StateListGetSet(
                    getState = { state.value.getAllItineraryServiceTask },
                    setState = { s -> state.update { it.copy(getAllItineraryServiceTask = s) } },
                ),
            ),
        )

        // Delete task Use Case
        collectLoading(
            flow = deleteTaskUseCase.state.map { it.loadingSet },
            setState = { state.update { s -> s.copy(deleteTaskLoading = it) } },
        )
        collectSuccessEvent(
            flow = deleteTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            stateListGetSet = arrayOf(
                StateListGetSet(
                    getState = { state.value.getAllItineraryTask },
                    setState = { s -> state.update { it.copy(getAllItineraryTask = s) } },
                ),
                StateListGetSet(
                    getState = { state.value.getAllItineraryServiceTask },
                    setState = { s -> state.update { it.copy(getAllItineraryServiceTask = s) } },
                ),
            ),
        )

        // Get Current User Profile Use Case
        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getProfile },
            setState = { newState -> state.update { it.copy(getProfile = newState) } },
        )

        collectSuccessEvent(
            flow = sendItineraryMessageUseCase.successEvent,
            getState = { state.value.getAllMessages },
            setState = { s ->
                state.update {
                    it.copy(
                        getAllMessages = State(data = s.data.orEmpty() + state.value.getAllMessages.data.orEmpty()),
                    )
                }
            },
        )
    }

    fun onEvent(event: EditItineraryEvent) {
        when (event) {
            is EditItineraryEvent.GetAllItinerary ->
                getAllItinerary()

            is EditItineraryEvent.EditItinerary ->
                updateProposal(event.proposal)

            is EditItineraryEvent.ClearItinerary ->
                clearItinerary()

            is EditItineraryEvent.ClearItineraryState ->
                clearItineraryState()

            is EditItineraryEvent.GetItinerary -> {
                getItinerary(event)
                getAllItineraryServiceByItinerary(event.id)
                getAllItineraryTask(event.id)
            }

            is EditItineraryEvent.GetAllItineraryService ->
                getAllItineraryServiceDate()

            is EditItineraryEvent.FilterItineraryServices ->
                filterItineraryServices(event)

            is EditItineraryEvent.GetAllItineraryServiceByDate ->
                getAllItineraryServiceByDate(event)

            is EditItineraryEvent.GetAllItineraryServiceByDateRange ->
                getAllItineraryServiceByDateRange(event)

            is EditItineraryEvent.AddItineraryService ->
                addItineraryService(event)

            is EditItineraryEvent.ClearItineraryService ->
                clearItineraryService()

            is EditItineraryEvent.SelectItineraryService ->
                selectItineraryService(event)

            is EditItineraryEvent.SelectAllItineraryService ->
                selectAllItineraryService()

            is EditItineraryEvent.ClickItineraryService ->
                clickItineraryService(event)

            is EditItineraryEvent.UpdateItineraryServiceStatus ->
                updateItineraryStatus(event)

            is EditItineraryEvent.UpdateSelectedItineraryServiceStatus ->
                updateSelectedItinerariesStatus(event)

            is EditItineraryEvent.UpdateItineraryService ->
                updateItineraryService(event)

            is EditItineraryEvent.AddClient ->
                addClient(event)

            is EditItineraryEvent.SetPrimaryClient ->
                setPrimaryClient(event)

            is EditItineraryEvent.EditClient ->
                editClient(event)

            is EditItineraryEvent.DeleteClient ->
                deleteClient(event)

            is EditItineraryEvent.UpdateClientDetails ->
                updateGuestDetails(event)

            is EditItineraryEvent.DeleteTask ->
                deleteTask(event)

            is EditItineraryEvent.EditTaskStatus ->
                editTaskStatus(event)

            is EditItineraryEvent.SendToAxus ->
                sendToAxus(event)

            is EditItineraryEvent.ClearTravelAgentState -> clearTravelAgentState()

            is EditItineraryEvent.UpdateProposal -> editProposal(event)

            is EditItineraryEvent.ClearProposalState -> clearProposalState()

            is EditItineraryEvent.GetItineraryServicesMessages -> getItineraryServiceMessage()
        }
    }

    /**
     * Profile
     */
    private fun getCurrentProfile() =
        scope.launch {
            getCurrentUserUseCase.get()
        }

    /**
     * Itinerary Service Events
     */

    private fun getAllItinerary() =
        getData(
            getData = { itineraryRepository.getAll() },
            getState = { state.value.getAllItinerary },
            setState = { newState -> state.update { it.copy(getAllItinerary = newState) } },
        )

    private fun getItinerary(event: EditItineraryEvent.GetItinerary) =
        getData(
            getData = { itineraryRepository.getAllByIds(listOf(event.id)) },
            getState = { state.value.getAllItinerary },
            setState = { newState -> state.update { it.copy(getAllItinerary = newState) } },
            onSuccess = { itineraries ->
                itineraries.firstOrNull()?.proposal?.coverImage?.id?.let {
                    getCoverImage(it)
                }
            },
        )

    private fun getCoverImage(id: String) {
        getData(
            getData = { fileRepository.getById(id) },
            getState = { state.value.getCoverImage },
            setState = { newState -> state.update { it.copy(getCoverImage = newState) } },
        )
    }

    private fun updateProposal(
        updatedProposal: ProposalModel,
    ) {
        if (updatedProposal.isEmpty) return

        getData(
            getData = { proposalRepository.updateProposal(updatedProposal) },
            getState = { state.value.editProposal },
            setState = { state.value = state.value.copy(editProposal = it) },
            onSuccess = { newProposal ->
                state.update {
                    it.copy(
                        getAllItinerary = it.getAllItinerary.copy(
                            data = it.getAllItinerary.data?.update(
                                predicate = { i -> i.proposal?.id == newProposal.id },
                                update = { i -> i.copy(proposal = newProposal) },
                            ),
                        ),
                    )
                }
            },
        )
    }

    private fun addItineraryService(event: EditItineraryEvent.AddItineraryService) {
        state.update {
            it.copy(
                addItineraryService = State.loading(),
            )
        }

        getData(
            getData = {
                itineraryRepository.addAllItineraryService(
                    itineraryServiceList = listOf(
                        ItineraryServiceModel.empty.copy(
                            itineraryId = event.itinerary.id,
                            service = event.service,
                            editedDescription = event.service.description ?: "",
                            startDate = event.itinerary.proposal?.fromDate ?: "",
                        ),
                    ),
                ).map { it.first() }
            },
            getState = { state.value.addItineraryService },
            setState = { newState -> state.update { it.copy(addItineraryService = newState) } },
            onSuccess = { itineraryService ->
                val updatedItineraryServices =
                    listOf(itineraryService) + state.value.getAllItineraryService.data.orEmpty()

                state.update { state ->
                    state.copy(
                        getAllItineraryService = state.getAllItineraryService.copy(
                            data = updatedItineraryServices,
                        ),
                    )
                }

                toastManager.success(
                    title = "Saved",
                    description = "New itinerary service has been saved",
                )
            },
            ignoreLoading = true,
        )
    }

    private fun clearItineraryService() {
        state.update {
            it.copy(
                addItineraryService = State(),
            )
        }
    }

    private fun clearItinerary() {
        state.update {
            it.copy(
                editProposal = State(),
            )
        }
    }

    private fun clearItineraryState() {
        state.update {
            it.copy(
                getAllItinerary = State(),
            )
        }
    }

    private fun selectItineraryService(event: EditItineraryEvent.SelectItineraryService) {
        state.update {
            it.copy(
                selectedItineraryServiceIds =
                    if (event.itineraryServiceId in it.selectedItineraryServiceIds)
                        it.selectedItineraryServiceIds - event.itineraryServiceId
                    else
                        it.selectedItineraryServiceIds + event.itineraryServiceId,
            )
        }
    }

    private fun selectAllItineraryService() {
        state.update {
            it.copy(
                selectedItineraryServiceIds =
                    if (it.selectedItineraryServiceIds.size == it.getAllItineraryService.data?.size)
                        emptySet()
                    else
                        it.getAllItineraryService.data
                            .orEmpty()
                            .map { itineraryServiceModel ->
                                itineraryServiceModel.id
                            }
                            .toSet(),
            )
        }
    }

    private fun clickItineraryService(event: EditItineraryEvent.ClickItineraryService) {
        state.update {
            it.copy(
                clickedItineraryServiceId = event.itineraryServiceId,
            )
        }
    }

    private fun updateSelectedItinerariesStatus(event: EditItineraryEvent.UpdateSelectedItineraryServiceStatus) {
        val allSelectedItineraryService = state.value.getAllItineraryService
            .data
            .orEmpty()
            .filter {
                it.id in state.value.selectedItineraryServiceIds
            }

        allSelectedItineraryService.forEach { itineraryService ->
            updateItineraryService(itineraryService) { i ->
                i.copy(
                    status = event.status,
                )
            }
        }
    }

    private fun updateItineraryStatus(event: EditItineraryEvent.UpdateItineraryServiceStatus) {
        updateItineraryService(event.itineraryService) { itineraryService ->
            itineraryService.copy(
                status = event.status,
            )
        }
    }

    private fun updateItineraryService(event: EditItineraryEvent.UpdateItineraryService) {
        updateItineraryService(event.itineraryService) {
            event.itineraryService
        }
    }

    private fun updateItineraryService(
        itineraryService: ItineraryServiceModel,
        update: (ItineraryServiceModel) -> ItineraryServiceModel,
    ) {
        val updatedItineraryService = update(itineraryService)

        scope.launch {
            updateItineraryServiceUseCase.update(
                itineraryService = updatedItineraryService,
            )
        }
    }

    private fun getAllItineraryServiceByItinerary(itineraryId: String) =
        getData(
            getData = { itineraryRepository.getAllItineraryService(itineraryId) },
            getState = { state.value.getAllItineraryService },
            setState = { newState -> state.update { it.copy(getAllItineraryService = newState) } },
            onSuccess = { returnedItineraryServices ->
                getAllItineraryServiceTask(
                    ids = returnedItineraryServices
                        .map { it.id }
                        .toSet(),
                )
            },
        )

    private fun getItineraryServiceMessage() {
        state.value.getAllItineraryService.data
            .orEmpty()
            .forEach { itineraryService ->
                getAllMessages(itineraryService.id)
            }
    }

    private fun getAllMessages(itineraryServiceId: String) =
        scope.launch {
            messageRepository
                .getMessagesByItineraryServiceId(id = itineraryServiceId)
                .onSuccess {
                    val listOfMessages =
                        (state.value.getAllMessages.data.orEmpty() + it).toSet()

                    state.update { state ->
                        state.copy(
                            getAllMessages = State.success(listOfMessages.toList()),
                        )
                    }
                }
        }

    private fun getAllItineraryServiceDate() {
        getAllItineraryServiceByDate(
            event = EditItineraryEvent.GetAllItineraryServiceByDate(
                date = state.value.startDate,
            ),
        )
    }

    private fun filterItineraryServices(event: EditItineraryEvent.FilterItineraryServices) {
        state.update {
            it.copy(
                filterOptions = event.filterOptions,
            )
        }
    }

    private suspend fun handleReturnedItineraryServices(returnedItineraryServices: List<ItineraryServiceModel>) {
        state.update {
            it.copy(
                getAllItineraryService = state.value.getAllItineraryService.copy(
                    data = (state.value.getAllItineraryService.data.orEmpty() + returnedItineraryServices),
                ),
            )
        }

        getAllItineraryServiceTask(
            ids = returnedItineraryServices
                .map { it.id }
                .toSet(),
        )

        // fetch requests to get TA
        val itineraryIds = returnedItineraryServices.mapNotNull { it.itineraryId }

        val listOfItineraries = itineraryRepository.getAllByIds(itineraryIds)
        listOfItineraries.getOrNull()?.let { itineraries ->
            state.update {
                it.copy(
                    getAllItinerary = state.value.getAllItinerary.copy(
                        data = itineraries,
                    ),
                )
            }
        }
    }

    private fun getAllItineraryServiceByDate(event: EditItineraryEvent.GetAllItineraryServiceByDate) {
        scope.launch {
            state.update {
                it.copy(
                    getAllItineraryService = State.loading(),
                    startDate = event.date,
                    selectedFromDate = null,
                    selectedToDate = null,
                )
            }

            loadingManager.emitLoading(true)

            itineraryRepository
                .getAllItineraryServiceByStartDate(
                    startDate = state.value.startDate.toString(),
                )
                .onSuccess { returnedItineraryServices ->
                    handleReturnedItineraryServices(returnedItineraryServices)
                }
                .onFailure {
                    state.update {
                        it.copy(
                            getAllItineraryService = State.failure(),
                        )
                    }
                }
        }.invokeOnCompletion {
            updateLoadingState(isLoading = false)
        }
    }

    private fun updateLoadingState(isLoading: Boolean) {
        scope.launch {
            loadingManager.emitLoading(isLoading)
            state.update { currentState ->
                currentState.copy(
                    getAllItineraryService = currentState.getAllItineraryService.copy(
                        isLoading = isLoading,
                    ),
                )
            }
        }
    }

    private fun getAllItineraryServiceByDateRange(event: EditItineraryEvent.GetAllItineraryServiceByDateRange) {
        scope.launch {
            if (event.startDate.isNullOrEmpty() &&
                event.endDate.isNullOrEmpty()
            ) {
                state.update {
                    it.copy(
                        selectedFromDate = null,
                        selectedToDate = null,
                    )
                }

                getAllItineraryServiceDate()

                return@launch
            }

            state.update {
                it.copy(
                    getAllItineraryService = State.loading(),
                    selectedFromDate = event.startDate,
                    selectedToDate = event.endDate,
                )
            }

            loadingManager.emitLoading(true)

            itineraryRepository
                .getAllItineraryServiceByDateRange(
                    startDate = fromDDTOMM(state.value.selectedFromDate.orEmpty()),
                    endDate = fromDDTOMM(state.value.selectedToDate.orEmpty()),
                )
                .onSuccess { returnedItineraryServices ->
                    handleReturnedItineraryServices(returnedItineraryServices)
                }
                .onFailure {
                    state.update {
                        it.copy(
                            getAllItineraryService = State.failure(),
                        )
                    }
                }
        }.invokeOnCompletion {
            updateLoadingState(isLoading = false)
        }
    }

    /**
     * Proposal events
     */

    private fun editProposal(event: EditItineraryEvent.UpdateProposal) {
        getData(
            getData = { proposalRepository.updateProposal(event.proposal) },
            getState = { state.value.addEditProposal },
            setState = { state.value = state.value.copy(addEditProposal = it) },
            onSuccess = { proposal ->
                val itineraryList = state.value.getAllItinerary.data.orEmpty().toMutableList()
                val proposalIndex = itineraryList.indexOfFirst { it.proposal?.id == proposal.id }
                if (proposalIndex != -1)
                    itineraryList[proposalIndex] = itineraryList[proposalIndex].copy(proposal = proposal)

                state.value = state.value.copy(
                    getAllItinerary = state.value.getAllItinerary.copy(
                        data = itineraryList,
                    ),
                )

                toastManager.success(description = "Proposal updated successfully")
            },
        )
    }

    private fun clearProposalState() {
        state.value = state.value.copy(
            addEditProposal = State(),
        )
    }

    /**
     * Client events
     */

    private fun getAllCategory() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { newState -> state.update { it.copy(getAllCategory = newState) } },
        )

    private fun getAllArea() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { newState -> state.update { it.copy(getAllArea = newState) } },
        )

    private fun getAllClient() =
        getData(
            getData = { clientRepository.getAll() },
            getState = { state.value.getAllClient },
            setState = { newState -> state.update { it.copy(getAllClient = newState) } },
        )

    private fun addClient(event: EditItineraryEvent.AddClient) {
        val updatedClients = listOf(event.client) + state.value.getAllClient.data.orEmpty()
        state.update {
            it.copy(
                getAllClient = it.getAllClient.copy(
                    data = updatedClients,
                ),
            )
        }

        val proposal = state.value.getAllItinerary.data?.firstOrNull()?.proposal

        if (proposal != null)
            updateProposal(
                updatedProposal = ProposalModel.empty.copy(
                    id = proposal.id,
                    owner = proposal.owner,
                    travelAgent = proposal.travelAgent,
                    clients = proposal.clients.orEmpty() + event.client.id,
                ),
            )
    }

    private fun editClient(event: EditItineraryEvent.EditClient) {
        state.update {
            it.copy(
                getAllClient = it.getAllClient.copy(
                    data = it.getAllClient.data.orEmpty().map { c ->
                        if (c.id == event.client.id) event.client else c
                    },
                ),
            )
        }
    }

    private fun setPrimaryClient(event: EditItineraryEvent.SetPrimaryClient) {
        val proposal = state.value.getAllItinerary.data?.firstOrNull()?.proposal

        if (proposal != null)
            updateProposal(
                updatedProposal = ProposalModel.empty.copy(
                    id = proposal.id,
                    owner = proposal.owner,
                    travelAgent = proposal.travelAgent,
                    primaryClient = event.client,
                ),
            )
    }

    private fun deleteClient(event: EditItineraryEvent.DeleteClient) {
        scope.launch {
            clientRepository.delete(event.client.id)
                .onSuccess {
                    state.update {
                        it.copy(
                            getAllClient = it.getAllClient.copy(
                                data = it.getAllClient.data.orEmpty().filter { client ->
                                    client.id != event.client.id
                                },
                            ),
                        )
                    }

                    val proposal = state.value.getAllItinerary.data?.firstOrNull()?.proposal

                    if (proposal != null)
                        updateProposal(
                            updatedProposal = ProposalModel.empty.copy(
                                id = proposal.id,
                                owner = proposal.owner,
                                travelAgent = proposal.travelAgent,
                                clients = proposal.clients.orEmpty().filter { id ->
                                    id != event.client.id
                                },
                                primaryClient =
                                    if (event.client.id == proposal.primaryClient?.id)
                                        ClientModel.empty
                                    else
                                        null,
                            ),
                        )
                }
        }
    }

    private fun updateGuestDetails(event: EditItineraryEvent.UpdateClientDetails) {
        val proposal = state.value.getAllItinerary.data?.firstOrNull()?.proposal

        if (proposal != null)
            updateProposal(
                updatedProposal = proposal.copy(guestDetails = event.guestDetails),
            )
    }

    /**
     * Task events
     */

    private fun getAllItineraryTask(itineraryId: String) {
        getData(
            getData = { taskRepository.getAllByItineraryId(itineraryId) },
            getState = { state.value.getAllItineraryTask },
            setState = { newState -> state.update { it.copy(getAllItineraryTask = newState) } },
        )
    }

    private fun getAllItineraryServiceTask(ids: Set<String>) {
        getData(
            getData = { taskRepository.getAllByItineraryServiceIds(ids.toList()) },
            getState = { state.value.getAllItineraryServiceTask },
            setState = { newState -> state.update { it.copy(getAllItineraryServiceTask = newState) } },
        )
    }

    private fun editTaskStatus(event: EditItineraryEvent.EditTaskStatus) =
        scope.launch {
            updateTaskUseCase.update(
                task = TaskModel.empty.copy(
                    id = event.taskId,
                    status = event.status,
                ),
            )
        }

    private fun deleteTask(event: EditItineraryEvent.DeleteTask) =
        scope.launch {
            deleteTaskUseCase.delete(
                task = event.task,
            )
        }

    /**
     * Messaging events
     */

    private fun sendToAxus(event: EditItineraryEvent.SendToAxus) {
        scope.launch {
            axusClient.insert(
                itinerary = AxusItinerary.testPushItinerary.copy(
                    title = event.itinerary.proposal?.title ?: "",
                    subtitle = "fourth test",
                    startDate = "2024-03-01",
                    endDate = "2024-03-10",
                    status = "APPROVED",
                    itineraryFor = "Sami Daik fourth",
                    itineraryPrice = "123.45",
                    collaborators = emptyList(),
                    travelers = emptyList(),
                    bookings = emptyList(),
                ),
            )
        }
    }

    /**
     * Travel agent events
     */
    private fun clearTravelAgentState() {
        state.update {
            it.copy(
                getAllTravelAgent = PaginationState(),
            )
        }
    }
}